import React, { FC, useCallback, useState } from 'react';
import {
  Breadcrumb,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  Row,
} from 'reactstrap';
import { AddressDistanceCriterionOptions } from '../../../../../shared/orders/raffle';
import { InlineHelp } from '../../../../components';
import { MaterialIconText } from '../../../../components/MaterialIconText';
import { UserLocation } from '../../../../models';
import { useTranslation } from '../../../../translations';
import { useCheckboxState } from '../../../../utils';
import { RaffleConfiguratorProps } from '../utils';
import { AddressSearch } from '.';

export const AddressDistance: FC<RaffleConfiguratorProps<AddressDistanceCriterionOptions>> = ({
  defaultOptions,
  displayName,
  onBack,
  onCancel,
  onConfigure,
}) => {
  const { ts } = useTranslation();
  const [address, setAddress] = useState<UserLocation | undefined>(defaultOptions?.origin);
  const [preferFar, setPreferFar] = useCheckboxState(defaultOptions?.preferFar ?? true);
  const onFinish = useCallback(() => {
    onConfigure({
      name: 'addressDistance',
      options: { origin: address, preferFar },
    });
  }, [onConfigure, address, preferFar]);

  const onAddressChange = useCallback((addr: UserLocation | undefined) => {
    setAddress(addr);
  }, []);

  return (
    <>
      <ModalBody>
        <Row>
          <Col xs={12}>
            <Breadcrumb
              items={[
                { text: `${ts('common.display_name')}: ${displayName ?? ts('none')}` },
                { text: ts('type_address_distance') },
              ]}
            />
            <MaterialIconText name="keyboard_backspace" onClick={onBack}>
              {ts('common.go_back')}
            </MaterialIconText>
            <hr />
          </Col>
          <Col xs={12}>
            <div>{ts('the_venue_address_will_be_searched_via_the_google_places_api')}</div>
          </Col>
          <Col xs={12}>
            <hr />
          </Col>
          <AddressSearch defaultLocation={address} onChange={onAddressChange} />
          <Col xs={12}>
            <hr />
          </Col>
          <Col xs={12}>
            <FormGroup check className="mt-10">
              <Input
                defaultChecked
                id="eventStore"
                name="eventStore"
                onChange={setPreferFar}
                type="checkbox"
              />
              <Label check for="eventStore">
                {ts('prefer_attendees_further_away_from_event')}
                <InlineHelp>
                  <p>
                    {ts(
                      'checking_this_box_will_weight_attendees_higher_the_further_away_from_the_event_venue_they_are',
                    )}
                  </p>
                  <p>{ts('when_unchecked_it_will_weight_attendees_lower_if_they_are')}</p>
                </InlineHelp>
              </Label>
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          className="editor-finish-btn"
          color="primary"
          disabled={!address}
          onClick={onFinish}
        >
          {ts('common.finish')}
        </Button>
        <Button className="editor-cancel-btn" color="secondary" onClick={onCancel} outline>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </>
  );
};
